import { useRef, useState, useCallback, useEffect } from "react";
import "./userlogin.scss";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Api from "../../../core/Api";
import { StorageHelpers } from "../../../core/Helpers";

const useLogin = () => {
  const refReCaptcha = useRef(null);
  const [showSmsValidationModal, setShowSmsValidationModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  useEffect(() => {
    const getBranches = async () => {
      setBranchesLoading(true);
      try {
        const response = await Api.getBranchesAsync();
        setBranches(response.data.data);
      } finally {
        setBranchesLoading(false);
      }
    };
    getBranches();
  }, []);

  const formik = useFormik({
    initialValues: {
      identityNr: "",
      gsm: "",
      gsmCode: "",
      gRecaptchaToken: null,
      isRegister: false,
      idBranch: 1,
    },
    validationSchema: Yup.object().shape({
      identityNr: Yup.string()
        .required("Bu alan boş bırakılamaz.")
        .matches(/^[0-9]+$/, "Geçersiz TC / Vergi numarası girdiniz.")
        .min(10, "TC / Vergi Numarası 10 haneden düşük olamaz.")
        .max(11)
        .nullable(false),
      gsm: Yup.string().required("Bu alan boş bırakılamaz."),
      idBranch: Yup.number().required("Bu alan boş bırakılamaz."),
    }),

    onSubmit: async (val) => {
      setIsLoading(true);
      const isCaptchaVerify = await StorageHelpers.isGoogleRecaptchaVerifyAsync({
        ref: refReCaptcha,
        token: val.gRecaptchaToken,
      });

      if (isCaptchaVerify) {
        const response = await Api.validateCustomerAsync(val);
        if (response.data.isSuccess) {
          toast.success("Başarıyla giriş yapıldı.", {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setIsLoading(false);
          setShowSmsValidationModal(true);
        } else {
          toast.error("Hatalı giriş yaptınız.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          setIsLoading(false);
        }
      } else {
        toast.error("Google Recaptcha güvenlik doğrulaması başarısız oldu. Lütfen tekrar deneyiniz..", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        setIsLoading(false);
      }
    },
  });

  const gRecaptchaVerify = useCallback(async ({ token }) => {
    await formik.setFieldValue("gRecaptchaToken", token);
  }, []);

  const form = {
    identityNr: {
      type: "text",
      value: formik.values.identityNr,
      onBlur: formik.handleBlur,
      onChange: formik.handleChange,
      placeholder: "TC Kimlik Numarası",
      maxLength: "11",
      id: "identityNr",
      name: "identityNr",
      className: ` ${
        formik.errors.identityNr && formik.touched.identityNr && "border-red-600"
      } rounded-md white text-base p-2 pl-4 border-2`,
    },
    gsm: {
      type: "text",
      id: "gsm",
      name: "gsm",
      placeholder: "Telefon Numarası",
      value: formik.values.gsm,
      onBlur: formik.handleBlur,
      mask: "999-999-99-99",
      onChange: formik.handleChange,
      className: `rounded-md white text-base p-2 pl-4  border-2 ${
        formik.touched.gsm && formik.errors.gsm && "border-red-600"
      }`,
    },
    loginButton: {
      disabled: isLoading,
      type: "submit",
      className: isLoading
        ? "bg-gray-600 w-full px-6 text-base py-3 rounded-md text-white hover:bg-gray-700 duration-200"
        : "bg-yellow-500 w-full px-6 text-base py-3 rounded-md text-white hover:bg-yellow-600 duration-200",
    },
    gRecaptcha: {
      onVerify: (token) => gRecaptchaVerify({ token: token }),
      ref: refReCaptcha,
    },
    idBranch: {
      onChange: (e) => formik.setFieldValue("idBranch", e.target.value),
      className: "rounded-md white text-base p-2 pl-4 border-2 w-full",
      value: formik.values.idBranch,
    },
  };

  return {
    form,
    formik,
    dispatch,
    navigate,
    setShowSmsValidationModal,
    showSmsValidationModal,
    isLoading,
    branches,
    branchesLoading,
  };
};

export default useLogin;
